import React, { createContext, useContext, useMemo, useEffect, useState } from "react";
import { createApp } from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge/utilities";
import { Redirect } from "@shopify/app-bridge/actions";
import axiosInstance from "../api/shopifyAPI";
import AuthRedirect from "./AuthRedirect";
import Loading from "./Loading";

const AppBridgeContext = createContext(null);

export function AppBridgeProvider({ children }) {
  const apiKey = "818a8b2c4beb5c3d540e0fdd172af11e";
  const host = new URLSearchParams(window.location.search).get("host");
  const shop = new URLSearchParams(window.location.search).get("shop");
  const baseUrl = "https://shopify-app.marketify.biz";

  const [isLoading, setIsLoading] = useState(true);
  const [isAuthRedirecting, setIsAuthRedirecting] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);

  console.log("API Key from env:", apiKey);
  console.log("Host from URL:", host);
  console.log("Shop from URL:", shop);

  // Initialize App Bridge
  const app = useMemo(() => {
    if (!apiKey || !host) {
      console.error("Shopify AppBridge config is invalid. Check API Key and Host.");
      return null;
    }
    return createApp({
      apiKey,
      host,
      forceRedirect: true,
    });
  }, [apiKey, host]);

  useEffect(() => {
    if (!app || !shop) return;

    const checkAuthAndToken = async () => {
      try {
        // First check if we have a token in our database
        const checkTokenresponse = await axiosInstance.get("/marketify/check-token", { params: { shop } });

        // Get session token regardless of database token status
        const token = await getSessionToken(app);
        setSessionToken(token);
console.log(checkTokenresponse)
        // If we don't have a token in the database, redirect to auth
        if (!checkTokenresponse.data.authToken) {
          setIsAuthRedirecting(true);
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, `${baseUrl}/auth?shop=${shop}`);
          return;
        }

        // Validate the session token
        const response = await fetch(`${baseUrl}/validate-session`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ sessionToken: token }),
        });

        const data = await response.json();
        
        if (!data.success) {
          // If validation fails, redirect to auth
          setIsAuthRedirecting(true);
          const redirect = Redirect.create(app);
          redirect.dispatch(Redirect.Action.REMOTE, `${baseUrl}/auth?shop=${shop}`);
          return;
        }

        setIsLoading(false);
      } catch (error) {
        console.error("Auth error:", error);
        // Redirect to auth on error
        setIsAuthRedirecting(true);
        const redirect = Redirect.create(app);
        redirect.dispatch(Redirect.Action.REMOTE, `${baseUrl}/auth?shop=${shop}`);
      }
    };

    checkAuthAndToken();
  }, [app, shop, baseUrl]);

  if (isAuthRedirecting) {
    return <AuthRedirect shop={shop} />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <AppBridgeContext.Provider value={{ app, sessionToken }}>
      {children}
    </AppBridgeContext.Provider>
  );
}

export function useShopifyAppBridge() {
  const context = useContext(AppBridgeContext);
  if (!context) {
    throw new Error("useShopifyAppBridge must be used within an AppBridgeProvider");
  }
  return context;
}

export default AppBridgeProvider;
